import { useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, RotateCcw, Loader2 } from "lucide-react";
import { ChatBubble } from "@/lib/aiChat";
import { useAiChat } from "@/hooks/useAiChat";

const SUGGESTIONS = ["How do I add FSC to MetaMask?", "How does the faucet work?", "How do I buy FMI?", "What is FBTC?"];

const Panel = ({ onClose }) => {
  const [input, setInput] = useState("");
  const { messages, busy, error, send, reset, endRef } = useAiChat({
    path: "/assistant/chat", historyPath: "/assistant/sessions", storageKey: "fsc_help_ai_session",
  });
  const submit = (text) => { setInput(""); send(text); };
  return (
    <div data-testid="help-chat-panel" className="fixed z-50 bottom-24 right-4 sm:right-6 w-[calc(100vw-2rem)] sm:w-[380px] h-[min(560px,calc(100vh-8rem))] flex flex-col rounded-xl border border-[#D4AF37]/30 bg-[#0A0A0A]/95 backdrop-blur-xl shadow-2xl shadow-black/60 animate-in fade-in slide-in-from-bottom-4 duration-200">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#2A2411]">
        <div>
          <p className="font-display font-semibold text-sm text-white">FSC Help</p>
          <p className="text-[10px] text-zinc-500">AI assistant · never share your seed phrase</p>
        </div>
        <div className="flex gap-1">
          <button data-testid="help-chat-new-btn" onClick={reset} disabled={busy} title="New chat" className="p-1.5 rounded text-zinc-400 hover:text-white"><RotateCcw size={15} /></button>
          <button data-testid="help-chat-close-btn" onClick={onClose} title="Close" className="p-1.5 rounded text-zinc-400 hover:text-white"><X size={16} /></button>
        </div>
      </div>
      <div data-testid="help-chat-messages" className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <div className="space-y-3">
            <p className="text-sm text-zinc-300">Hi! Ask me about the faucet, buying FMI, or connecting your wallet.</p>
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button key={s} data-testid="help-chat-suggestion" onClick={() => submit(s)}
                  className="text-xs px-3 py-1.5 rounded-full border border-[#2A2411] text-zinc-300 hover:border-[#D4AF37]/60 hover:text-white transition-colors">{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => <ChatBubble key={m.id} m={m} />)}
        <div ref={endRef} />
      </div>
      {error && <p data-testid="help-chat-error" className="px-4 pb-2 text-xs text-red-400">{error}</p>}
      <form onSubmit={(e) => { e.preventDefault(); submit(input); }} className="flex gap-2 p-3 border-t border-[#2A2411]">
        <input data-testid="help-chat-input" value={input} onChange={(e) => setInput(e.target.value)} maxLength={1000}
          placeholder="Type your question…" className="flex-1 min-w-0 rounded-md bg-[#050505] border border-[#2A2411] px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#D4AF37]/60" />
        <button data-testid="help-chat-send-btn" type="submit" disabled={busy || !input.trim()}
          className="shrink-0 w-10 h-10 grid place-items-center rounded-md bg-[#D4AF37] text-black hover:bg-[#F3E5AB] disabled:opacity-50 transition-colors">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </form>
    </div>
  );
};

export const HelpChat = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return null;
  return (
    <>
      {open && <Panel onClose={() => setOpen(false)} />}
      <button data-testid="help-chat-toggle" onClick={() => setOpen((o) => !o)} title={open ? "Close help" : "Ask FSC Help"}
        className="fixed z-50 bottom-5 right-4 sm:right-6 w-14 h-14 rounded-full grid place-items-center bg-[#D4AF37] text-black shadow-lg shadow-[#D4AF37]/20 hover:scale-105 hover:bg-[#F3E5AB] transition-transform">
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>
    </>
  );
};
